import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { toast } from "sonner";
import {
    Zap, LogIn, ClipboardCheck, RefreshCw, Users, TrendingUp,
    Clock, ChevronRight,
} from "lucide-react";
import { formatDateTime, relativeTime } from "@/lib/format";
import { Link } from "react-router-dom";

const STAGE_STYLES = {
    requested: "bg-slate-100 text-slate-600",
    approved: "bg-orange-50 text-orange-700",
    logged_in: "bg-sky-50 text-sky-700",
    wizard_done: "bg-emerald-50 text-emerald-700",
};

const STAGE_LABEL = {
    requested: "Kërkuar",
    approved: "Miratuar",
    logged_in: "U kyç",
    wizard_done: "Wizard i përfunduar",
};

export default function SuperOnboardingAnalytics() {
    const [data, setData] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [a, r] = await Promise.all([
                api.get("/superadmin/onboarding/analytics"),
                api.get("/superadmin/onboarding/recent?limit=25"),
            ]);
            setData(a.data);
            setRecent(r.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const fmtHours = (h) => {
        if (h == null) return "—";
        if (h < 1) return `${Math.round(h * 60)}m`;
        if (h < 24) return `${h.toFixed(1)}h`;
        return `${(h / 24).toFixed(1)}d`;
    };

    return (
        <>
            <Topbar
                title="Analitika e Onboarding-ut"
                subtitle="Ndjekje e kohës nga kërkesa demo → hotel aktiv"
            />
            <div className="flex-1 overflow-auto p-6" data-testid="super-onboarding-analytics">
                {/* KPI top strip */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="onboarding-kpis">
                    <Kpi label="Total kërkesa demo" value={data?.totals?.total_requests ?? "—"} tone="slate" icon={Users} loading={loading} />
                    <Kpi label="Miratuar (konvertuar)" value={data?.totals?.converted ?? "—"} tone="orange" icon={Zap} loading={loading} />
                    <Kpi label="Kyçje e parë" value={data?.totals?.first_login ?? "—"} tone="sky" icon={LogIn} loading={loading} />
                    <Kpi label="Wizard i përfunduar" value={data?.totals?.wizard_completed ?? "—"} tone="emerald" icon={ClipboardCheck} loading={loading} />
                </div>

                {/* Funnel */}
                <div className="mt-6 grid gap-4 lg:grid-cols-3">
                    <div className="akm-card p-5 lg:col-span-2" data-testid="onboarding-funnel">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-[13px] font-semibold text-slate-900">Hinka e konvertimit</div>
                                <div className="text-[11.5px] text-muted-foreground">Nga dorëzimi i formularit deri te Setup Wizard i përfunduar</div>
                            </div>
                            <button onClick={load} disabled={loading}
                                className="akm-btn-ghost h-8 text-[12px] disabled:opacity-50"
                                data-testid="onboarding-refresh">
                                <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Rifresko
                            </button>
                        </div>
                        <Funnel data={data} />
                    </div>

                    {/* Conversion rates card */}
                    <div className="akm-card p-5" data-testid="onboarding-rates">
                        <div className="mb-3 text-[13px] font-semibold text-slate-900">Përqindjet e konvertimit</div>
                        <RateRow label="Dorëzim → Miratim" pct={data?.funnel?.conversion_pct?.submit_to_approve} />
                        <RateRow label="Miratim → Kyçje e parë" pct={data?.funnel?.conversion_pct?.approve_to_login} />
                        <RateRow label="Kyçje → Wizard" pct={data?.funnel?.conversion_pct?.login_to_wizard} />
                        <div className="mt-4 border-t border-slate-100 pt-3">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Përfundim total</div>
                            <div className="mt-1 text-3xl font-semibold text-emerald-700">
                                {data?.funnel?.conversion_pct?.overall ?? 0}
                                <span className="ml-0.5 text-lg opacity-70">%</span>
                            </div>
                            <div className="text-[11px] text-muted-foreground">Kërkesë → Wizard i përfunduar</div>
                        </div>
                    </div>
                </div>

                {/* Timing cards */}
                <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="onboarding-timing">
                    <TimingCard title="Dorëzim → Miratim"
                        data={data?.timing_hours?.submit_to_approve} fmt={fmtHours}
                        tone="orange" icon={Zap} />
                    <TimingCard title="Miratim → Kyçje e parë"
                        data={data?.timing_hours?.approve_to_first_login} fmt={fmtHours}
                        tone="sky" icon={LogIn} />
                    <TimingCard title="Miratim → Wizard i përfunduar"
                        data={data?.timing_hours?.approve_to_wizard} fmt={fmtHours}
                        tone="emerald" icon={ClipboardCheck} />
                </div>

                {/* Recent conversions table */}
                <div className="mt-6 akm-card overflow-hidden">
                    <div className="border-b border-slate-100 px-5 py-3">
                        <div className="text-[13px] font-semibold text-slate-900">Konvertimet e fundit</div>
                        <div className="text-[11.5px] text-muted-foreground">{recent.length} kërkesat më të fundit të miratuara</div>
                    </div>
                    <table className="akm-table w-full">
                        <thead>
                            <tr>
                                <th className="text-left">Hoteli</th>
                                <th className="text-left">Faza</th>
                                <th className="text-left">Miratuar</th>
                                <th className="text-left">Kyçja e parë</th>
                                <th className="text-left">Wizard</th>
                                <th className="text-left">Dorëzim→Kyçje</th>
                                <th className="w-8"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="py-12 text-center text-muted-foreground">
                                        <TrendingUp size={32} className="mx-auto mb-2 opacity-40" />
                                        <div className="text-[13px]">Ende s&apos;ka kërkesa të miratuara.</div>
                                        <div className="text-[11.5px]">Miratoni leads nga <Link className="text-[#ff7b02] font-semibold" to="/admin/demo-requests">Kërkesat Demo</Link> për t&apos;i parë këtu.</div>
                                    </td>
                                </tr>
                            )}
                            {recent.map((r) => (
                                <tr key={r.request_id} data-testid={`onboarding-row-${r.request_id}`}>
                                    <td>
                                        <div className="font-medium text-slate-900">{r.hotel_name || "—"}</div>
                                        <div className="text-[11px] text-muted-foreground">
                                            {r.email} {r.country ? `· ${r.country}` : ""}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`inline-block rounded-md px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${STAGE_STYLES[r.stage] || STAGE_STYLES.approved}`}>
                                            {STAGE_LABEL[r.stage] || r.stage}
                                        </span>
                                    </td>
                                    <td className="text-[12px] text-slate-700">
                                        {r.approved_at ? relativeTime(r.approved_at) : "—"}
                                    </td>
                                    <td className="text-[12px] text-slate-700">
                                        {r.first_login_at ? (
                                            <>
                                                <div>{relativeTime(r.first_login_at)}</div>
                                                {r.hours_approve_to_login != null && (
                                                    <div className="text-[10.5px] text-muted-foreground">
                                                        <Clock size={9} className="inline" /> {fmtHours(r.hours_approve_to_login)} pas miratimit
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-muted-foreground">jo ende</span>
                                        )}
                                    </td>
                                    <td className="text-[12px] text-slate-700">
                                        {r.wizard_completed_at ? (
                                            <>
                                                <div>{relativeTime(r.wizard_completed_at)}</div>
                                                {r.hours_approve_to_wizard != null && (
                                                    <div className="text-[10.5px] text-muted-foreground">
                                                        <Clock size={9} className="inline" /> {fmtHours(r.hours_approve_to_wizard)} pas miratimit
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-muted-foreground">jo ende</span>
                                        )}
                                    </td>
                                    <td className="text-[12px] font-mono text-slate-700">
                                        {r.hours_submit_to_approve != null ? fmtHours(r.hours_submit_to_approve) : "—"}
                                    </td>
                                    <td className="text-right">
                                        {r.hotel_id && (
                                            <Link
                                                to={`/admin/hotels/${r.hotel_id}`}
                                                className="akm-btn-ghost h-7 w-7 inline-flex items-center justify-center"
                                                data-testid={`onboarding-view-hotel-${r.request_id}`}
                                            >
                                                <ChevronRight size={12} />
                                            </Link>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

const TONE_BG = {
    slate: "text-slate-800", orange: "text-orange-700", sky: "text-sky-700",
    emerald: "text-emerald-700",
};
const TONE_ICON = {
    slate: "text-slate-400", orange: "text-orange-500", sky: "text-sky-500",
    emerald: "text-emerald-500",
};

function Kpi({ label, value, tone, icon: Icon, loading }) {
    return (
        <div className="akm-card p-4">
            <div className="flex items-center justify-between">
                <div className="akm-section-title">{label}</div>
                {Icon && <Icon size={14} className={TONE_ICON[tone]} />}
            </div>
            <div className={`mt-1 text-2xl font-semibold tracking-tight ${TONE_BG[tone]} ${loading ? "opacity-50" : ""}`}>
                {value}
            </div>
        </div>
    );
}

function Funnel({ data }) {
    const labels = data?.funnel?.step_labels || ["Kërkesa", "Miratuara", "Kyçje e parë", "Wizard i përfunduar"];
    const steps = data?.funnel?.steps || [0, 0, 0, 0];
    const max = Math.max(...steps, 1);
    const COLORS = ["#94a3b8", "#ff7b02", "#0ea5e9", "#10b981"];
    return (
        <div className="mt-4 space-y-2.5">
            {labels.map((lbl, i) => {
                const val = steps[i] || 0;
                const pct = max > 0 ? Math.round((val / max) * 100) : 0;
                const pctOfPrev = i > 0 && steps[i - 1] > 0
                    ? Math.round((val / steps[i - 1]) * 100) : null;
                return (
                    <div key={lbl}>
                        <div className="mb-1 flex items-center justify-between text-[12px]">
                            <span className="font-semibold text-slate-800">{lbl}</span>
                            <span className="font-mono text-slate-700">
                                {val}
                                {pctOfPrev != null && (
                                    <span className="ml-2 text-[10.5px] text-muted-foreground">
                                        ({pctOfPrev}% nga hapi i mëparshëm)
                                    </span>
                                )}
                            </span>
                        </div>
                        <div className="h-6 w-full overflow-hidden rounded-md bg-slate-100">
                            <div
                                className="h-full rounded-md transition-all duration-500"
                                style={{ width: `${pct}%`, background: COLORS[i] }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function RateRow({ label, pct }) {
    return (
        <div className="mb-2.5 last:mb-0">
            <div className="mb-1 flex items-center justify-between text-[12px]">
                <span className="text-slate-700">{label}</span>
                <span className="font-mono font-semibold text-slate-800">{pct ?? 0}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                    className="h-full rounded-full bg-[#ff7b02] transition-all duration-500"
                    style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%` }}
                />
            </div>
        </div>
    );
}

function TimingCard({ title, data, fmt, tone, icon: Icon }) {
    return (
        <div className="akm-card p-4">
            <div className="flex items-center justify-between">
                <div className="text-[12px] font-semibold text-slate-800">{title}</div>
                {Icon && <Icon size={13} className={TONE_ICON[tone]} />}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">Mediana</div>
                    <div className={`mt-0.5 text-lg font-semibold tracking-tight ${TONE_BG[tone]}`}>
                        {fmt(data?.median)}
                    </div>
                </div>
                <div>
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">Mesatarja</div>
                    <div className={`mt-0.5 text-lg font-semibold tracking-tight ${TONE_BG[tone]}`}>
                        {fmt(data?.avg)}
                    </div>
                </div>
            </div>
            <div className="mt-2 text-[10.5px] text-muted-foreground">
                Kampion: {data?.sample_size ?? 0}
            </div>
        </div>
    );
}
